import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FaPlus, FaPen } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  assignToolsToEmployee,
  createTool,
  listEmployees,
  listTools,
  updateTool,
  withdrawToolsFromEmployee,
} from "../../api/tools";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";
import { InviteModal, SuccessModal } from "../../components/modal";
import PageIntroNav from "../../components/navigation/PageIntroNav";
import { getApiBaseUrl } from "../../data/authStorage";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import PaginationControls from "../../components/PaginationControls";

const EMPLOYEE_PAGE_SIZE = 8;

const TOOL_CACHE_KEY = "cehr_company_tools_v1";
const EMPLOYEE_CACHE_KEY = "cehr_company_tool_employees_v1";

const formatEmployeeName = (employee) => {
  if (!employee) {
    return "Unnamed staff";
  }

  return (
    employee.fullName ||
    employee.name ||
    `${employee.firstName || ""} ${employee.lastName || ""}`.trim() ||
    "Unnamed staff"
  );
};

const getAssignedTools = (employee) => {
  if (!employee || !Array.isArray(employee.toolAssignments)) {
    return [];
  }

  return employee.toolAssignments
    .filter((assignment) => assignment?.status === "Assigned")
    .map((assignment) => assignment?.tool)
    .filter(Boolean);
};

const Tools = () => {
  const navigate = useNavigate();
  const apiBaseUrl = getApiBaseUrl();

  const cachedTools = useMemo(() => readSessionCache(TOOL_CACHE_KEY, { tools: [] }), []);
  const cachedEmployees = useMemo(() => readSessionCache(EMPLOYEE_CACHE_KEY, { employees: [] }), []);

  const [tools, setTools] = useState(cachedTools?.tools || []);
  const [employees, setEmployees] = useState(cachedEmployees?.employees || []);
  const [isLoadingTools, setIsLoadingTools] = useState(!cachedTools?.tools?.length);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(!cachedEmployees?.employees?.length);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTool, setSelectedTool] = useState(null);
  const [toolForm, setToolForm] = useState({ name: "", description: "", isActive: true });
  const [isToolModalOpen, setIsToolModalOpen] = useState(false);
  const [isToolSubmitting, setIsToolSubmitting] = useState(false);
  const [assignmentEmployeeId, setAssignmentEmployeeId] = useState("");
  const [assignmentMode, setAssignmentMode] = useState("assign");
  const [selectedToolIds, setSelectedToolIds] = useState([]);
  const [isAssignmentModalOpen, setIsAssignmentModalOpen] = useState(false);
  const [isAssignmentSubmitting, setIsAssignmentSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [employeePage, setEmployeePage] = useState(1);

  const employeeTotalPages = Math.max(1, Math.ceil(employees.length / EMPLOYEE_PAGE_SIZE));
  const safeEmployeePage = Math.min(employeePage, employeeTotalPages);
  const paginatedEmployees = useMemo(
    () => employees.slice((safeEmployeePage - 1) * EMPLOYEE_PAGE_SIZE, safeEmployeePage * EMPLOYEE_PAGE_SIZE),
    [employees, safeEmployeePage],
  );

  const selectedEmployee = useMemo(
    () => employees.find((employee) => employee.id === assignmentEmployeeId) || null,
    [assignmentEmployeeId, employees],
  );

  const selectedEmployeeTools = useMemo(() => getAssignedTools(selectedEmployee), [selectedEmployee]);

  const assignedToolUsage = useMemo(() => {
    const usage = new Map();

    employees.forEach((employee) => {
      getAssignedTools(employee).forEach((tool) => {
        usage.set(tool.id, (usage.get(tool.id) || 0) + 1);
      });
    });

    return usage;
  }, [employees]);

  const loadTools = useCallback(async ({ silent = false } = {}) => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (!silent) {
        setIsLoadingTools(true);
      }
      setErrorMessage("");

      const response = await listTools();
      const nextTools = Array.isArray(response?.tools) ? response.tools : [];
      setTools(nextTools);
      writeSessionCache(TOOL_CACHE_KEY, { tools: nextTools });
    } catch (error) {
      const responseMessage = error?.message || error?.response?.data?.message || "Unable to load tools.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsLoadingTools(false);
    }
  }, [apiBaseUrl]);

  const loadEmployees = useCallback(async ({ silent = false } = {}) => {
    if (!apiBaseUrl) {
      setErrorMessage("API base URL is not configured.");
      return;
    }

    try {
      if (!silent) {
        setIsLoadingEmployees(true);
      }

      const response = await listEmployees({ status: "approved" });
      const nextEmployees = Array.isArray(response?.employees) ? response.employees : [];
      setEmployees(nextEmployees);
      writeSessionCache(EMPLOYEE_CACHE_KEY, { employees: nextEmployees });
    } catch (error) {
      const responseMessage = error?.message || error?.response?.data?.message || "Unable to load employees.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsLoadingEmployees(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    void loadTools({ silent: true });
    void loadEmployees({ silent: true });
  }, [loadEmployees, loadTools]);

  useEffect(() => {
    const refreshOnFocus = () => {
      if (document.visibilityState !== "hidden") {
        void loadTools({ silent: true });
        void loadEmployees({ silent: true });
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnFocus);

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnFocus);
    };
  }, [loadEmployees, loadTools]);

  const openCreateToolModal = () => {
    setSelectedTool(null);
    setToolForm({ name: "", description: "", isActive: true });
    setErrorMessage("");
    setIsToolModalOpen(true);
  };

  const openEditToolModal = (tool) => {
    setSelectedTool(tool);
    setToolForm({
      name: tool?.name || "",
      description: tool?.description || "",
      isActive: tool?.isActive ?? true,
    });
    setErrorMessage("");
    setIsToolModalOpen(true);
  };

  const closeToolModal = () => {
    setIsToolModalOpen(false);
    setSelectedTool(null);
    setToolForm({ name: "", description: "", isActive: true });
  };

  const submitToolForm = async (event) => {
    event.preventDefault();

    if (!toolForm.name.trim()) {
      setErrorMessage("Tool name is required.");
      return;
    }

    try {
      setIsToolSubmitting(true);
      setErrorMessage("");

      if (selectedTool?.id) {
        await updateTool(selectedTool.id, {
          toolName: toolForm.name.trim(),
          toolDescription: toolForm.description.trim(),
          isActive: toolForm.isActive,
        });
        setSuccessMessage("Tool updated successfully.");
      } else {
        await createTool({
          toolName: toolForm.name.trim(),
          toolDescription: toolForm.description.trim(),
        });
        setSuccessMessage("Tool created successfully.");
      }

      setIsSuccessOpen(true);
      closeToolModal();
      await loadTools({ silent: true });
    } catch (error) {
      const responseMessage = error?.message || error?.response?.data?.message || "Unable to save tool.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsToolSubmitting(false);
    }
  };

  const toggleToolStatus = async (tool) => {
    if (!tool?.id) {
      return;
    }

    try {
      setErrorMessage("");
      await updateTool(tool.id, { isActive: !tool.isActive });
      setSuccessMessage(tool.isActive ? "Tool archived successfully." : "Tool reactivated successfully.");
      setIsSuccessOpen(true);
      await loadTools({ silent: true });
    } catch (error) {
      const responseMessage = error?.message || error?.response?.data?.message || "Unable to update tool status.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    }
  };

  const openAssignmentModal = (employee, mode = "assign") => {
    setAssignmentEmployeeId(employee?.id || "");
    setAssignmentMode(mode);
    setSelectedToolIds(mode === "withdraw" ? getAssignedTools(employee).map((tool) => tool.id) : []);
    setErrorMessage("");
    setIsAssignmentModalOpen(true);
  };

  const closeAssignmentModal = () => {
    setIsAssignmentModalOpen(false);
    setAssignmentEmployeeId("");
    setAssignmentMode("assign");
    setSelectedToolIds([]);
  };

  const toggleToolSelection = (toolId) => {
    setSelectedToolIds((current) => (
      current.includes(toolId)
        ? current.filter((value) => value !== toolId)
        : [...current, toolId]
    ));
  };

  const submitAssignment = async (event) => {
    event.preventDefault();

    if (!selectedEmployee?.id) {
      setErrorMessage("Select an employee first.");
      return;
    }

    if (selectedToolIds.length === 0) {
      setErrorMessage("Select at least one tool.");
      return;
    }

    try {
      setIsAssignmentSubmitting(true);
      setErrorMessage("");

      if (assignmentMode === "withdraw") {
        await withdrawToolsFromEmployee(selectedEmployee.id, selectedToolIds);
        setSuccessMessage(`Tools withdrawn from ${formatEmployeeName(selectedEmployee)}.`);
      } else {
        await assignToolsToEmployee(selectedEmployee.id, selectedToolIds);
        setSuccessMessage(`Tools assigned to ${formatEmployeeName(selectedEmployee)}.`);
      }

      setIsSuccessOpen(true);
      closeAssignmentModal();
      await loadEmployees({ silent: true });
    } catch (error) {
      const responseMessage = error?.message || error?.response?.data?.message || "Unable to update tool assignments.";
      setErrorMessage(Array.isArray(responseMessage) ? responseMessage.join(", ") : responseMessage);
    } finally {
      setIsAssignmentSubmitting(false);
    }
  };

  const activeTools = tools.filter((tool) => tool.isActive);
  const inactiveTools = tools.filter((tool) => !tool.isActive);
  const totalAssignedTools = employees.reduce((count, employee) => count + getAssignedTools(employee).length, 0);

  return (
    <div className="dashboard-enter p-6">
      <PageIntroNav
        breadcrumbs={[
          { label: "Company Settings", path: "/company-settings" },
          { label: "Tools" },
        ]}
        title="Tools"
        subtitle="Register company tools, assign them to staff, and keep withdrawals visible in employee profiles."
        fallbackPath="/company-settings"
        backLabel="Back to Company Settings"
        actions={(
          <button
            type="button"
            onClick={openCreateToolModal}
            className="inline-flex items-center gap-2 rounded-xl bg-[#1234CD] px-5 py-3 text-white shadow-sm transition hover:opacity-95"
          >
            <FaPlus />
            Add tool
          </button>
        )}
      />

      {errorMessage ? (
        <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {errorMessage}
        </div>
      ) : null}

      <div className="mb-6 grid gap-4 md:grid-cols-4">
        {[
          { label: "Registered tools", value: tools.length },
          { label: "Active tools", value: activeTools.length },
          { label: "Inactive tools", value: inactiveTools.length },
          { label: "Assigned placements", value: totalAssignedTools },
        ].map((stat) => (
          <div key={stat.label} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm text-slate-500">{stat.label}</p>
            <p className="mt-2 text-2xl font-semibold text-[#071C50]">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.3fr_0.9fr]">
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold text-[#071C50]">Tool registry</h2>
              <p className="text-sm text-slate-500">Manage the inventory of tools your organization can assign.</p>
            </div>
          </div>

          {isLoadingTools ? (
            <FetchLoadingState message="Loading tools..." className="min-h-[220px]" />
          ) : tools.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              No tools registered yet. Add the first tool to begin assigning equipment.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead>
                  <tr className="text-left text-slate-500">
                    <th className="py-3 pr-4 font-medium">Tool</th>
                    <th className="py-3 pr-4 font-medium">Description</th>
                    <th className="py-3 pr-4 font-medium">Status</th>
                    <th className="py-3 pr-4 font-medium">Assigned</th>
                    <th className="py-3 pr-4 font-medium text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tools.map((tool) => (
                    <tr key={tool.id} className="align-top">
                      <td className="py-4 pr-4">
                        <p className="font-medium text-[#071C50]">{tool.name}</p>
                      </td>
                      <td className="py-4 pr-4 text-slate-600">
                        {tool.description || "-"}
                      </td>
                      <td className="py-4 pr-4">
                        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-medium ${tool.isActive ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {tool.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-4 pr-4 text-slate-600">
                        {assignedToolUsage.get(tool.id) || 0} employee{assignedToolUsage.get(tool.id) === 1 ? "" : "s"}
                      </td>
                      <td className="py-4 pr-0 text-right">
                        <div className="inline-flex flex-wrap items-center justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openEditToolModal(tool)}
                            className="inline-flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-slate-700 transition hover:border-[#1234CD] hover:text-[#1234CD]"
                          >
                            <FaPen className="text-xs" />
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => toggleToolStatus(tool)}
                            className="rounded-xl border border-slate-200 px-3 py-2 text-slate-700 transition hover:border-[#1234CD] hover:text-[#1234CD]"
                          >
                            {tool.isActive ? "Deactivate" : "Reactivate"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-[#071C50]">Employee assignments</h2>
            <p className="text-sm text-slate-500">Review current tool placements and manage assignment or withdrawal per employee.</p>
          </div>

          {isLoadingEmployees ? (
            <FetchLoadingState message="Loading employees..." className="min-h-[220px]" />
          ) : employees.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
              No approved employees were found for tool assignments.
            </div>
          ) : (
            <>
            <div className="space-y-3 max-h-[640px] overflow-y-auto pr-1">
              {paginatedEmployees.map((employee) => {
                const assignedTools = getAssignedTools(employee);

                return (
                  <div key={employee.id} className="rounded-2xl border border-slate-200 p-4">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-semibold text-[#071C50]">{formatEmployeeName(employee)}</p>
                        <p className="text-sm text-slate-500">{employee.positionName || employee.departmentName || "Staff"}</p>
                        <p className="text-xs text-slate-400">{employee.officialEmail || employee.personalEmail || "No email available"}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => navigate(`/employee/employeespage?id=${encodeURIComponent(employee.id)}`)}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700 transition hover:border-[#1234CD] hover:text-[#1234CD]"
                      >
                        View profile
                      </button>
                    </div>

                    <div className="mb-4 flex flex-wrap gap-2">
                      {assignedTools.length > 0 ? (
                        assignedTools.map((tool) => (
                          <span key={tool.id} className="rounded-full bg-[#EEF2FF] px-3 py-1 text-xs font-medium text-[#1234CD]">
                            {tool.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-sm text-slate-500">No tools assigned</span>
                      )}
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => openAssignmentModal(employee, "assign")}
                        className="rounded-xl bg-[#1234CD] px-3 py-2 text-sm text-white"
                      >
                        Assign tools
                      </button>
                      <button
                        type="button"
                        onClick={() => openAssignmentModal(employee, "withdraw")}
                        className="rounded-xl border border-slate-200 px-3 py-2 text-sm text-slate-700"
                        disabled={assignedTools.length === 0}
                      >
                        Withdraw tools
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <PaginationControls page={safeEmployeePage} pageSize={EMPLOYEE_PAGE_SIZE} totalItems={employees.length} onPageChange={setEmployeePage} />
            </>
          )}
        </div>
      </div>

      <InviteModal isOpen={isToolModalOpen} onClose={closeToolModal}>
        <div className="my-6">
          <p className="text-2xl font-semibold text-black font-Outfit">
            {selectedTool ? "Edit Tool" : "Create Tool"}
          </p>
          <form className="mt-8 space-y-5" onSubmit={submitToolForm}>
            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Tool name</span>
              <input
                value={toolForm.name}
                onChange={(event) => setToolForm((current) => ({ ...current, name: event.target.value }))}
                className="w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#1234CD]"
                placeholder="e.g. Laptop"
              />
            </label>

            <label className="block">
              <span className="mb-2 block text-sm font-medium text-slate-700">Description</span>
              <textarea
                value={toolForm.description}
                onChange={(event) => setToolForm((current) => ({ ...current, description: event.target.value }))}
                className="min-h-[120px] w-full rounded-2xl border border-slate-200 px-4 py-3 outline-none focus:border-[#1234CD]"
                placeholder="Describe what this tool is used for"
              />
            </label>

            {selectedTool ? (
              <label className="inline-flex items-center gap-3 text-sm font-medium text-slate-700">
                <input
                  type="checkbox"
                  checked={toolForm.isActive}
                  onChange={(event) => setToolForm((current) => ({ ...current, isActive: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-[#1234CD] focus:ring-[#1234CD]"
                />
                Tool is active
              </label>
            ) : null}

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={closeToolModal} className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                Cancel
              </button>
              <button type="submit" disabled={isToolSubmitting} className="rounded-xl bg-[#1234CD] px-5 py-3 text-sm text-white disabled:opacity-60">
                {isToolSubmitting ? "Saving..." : selectedTool ? "Update Tool" : "Create Tool"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <InviteModal isOpen={isAssignmentModalOpen} onClose={closeAssignmentModal}>
        <div className="my-6">
          <p className="text-2xl font-semibold text-black font-Outfit">
            {assignmentMode === "withdraw" ? "Withdraw Tools" : "Assign Tools"}
          </p>
          <p className="mt-2 text-sm text-slate-500">
            {selectedEmployee ? `${formatEmployeeName(selectedEmployee)} • ${selectedEmployee.positionName || selectedEmployee.departmentName || "Staff"}` : "Choose tools for the selected employee."}
          </p>

          <form className="mt-8 space-y-5" onSubmit={submitAssignment}>
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <p className="text-sm font-medium text-slate-700">Choose tools</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {tools.filter((tool) => assignmentMode === "withdraw" ? selectedEmployeeTools.some((item) => item.id === tool.id) : true).map((tool) => {
                  const isAssigned = selectedEmployeeTools.some((item) => item.id === tool.id);
                  return (
                    <label key={tool.id} className="flex cursor-pointer items-start gap-3 rounded-2xl border border-slate-200 bg-white p-3">
                      <input
                        type="checkbox"
                        checked={selectedToolIds.includes(tool.id)}
                        onChange={() => toggleToolSelection(tool.id)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-[#1234CD] focus:ring-[#1234CD]"
                      />
                      <div>
                        <p className="font-medium text-[#071C50]">{tool.name}</p>
                        <p className="text-xs text-slate-500">{tool.description || "No description"}</p>
                        <p className="mt-1 text-xs text-slate-400">{tool.isActive ? "Active" : "Inactive"}{isAssigned ? " • Assigned" : ""}</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              {tools.length === 0 ? <p className="mt-3 text-sm text-slate-500">Create a tool first to enable assignments.</p> : null}
            </div>

            <div className="flex items-center justify-end gap-3 pt-2">
              <button type="button" onClick={closeAssignmentModal} className="rounded-xl border border-slate-200 px-4 py-3 text-sm text-slate-700">
                Cancel
              </button>
              <button type="submit" disabled={isAssignmentSubmitting} className="rounded-xl bg-[#1234CD] px-5 py-3 text-sm text-white disabled:opacity-60">
                {isAssignmentSubmitting ? "Saving..." : assignmentMode === "withdraw" ? "Withdraw Selected" : "Assign Selected"}
              </button>
            </div>
          </form>
        </div>
      </InviteModal>

      <SuccessModal
        isOpen={isSuccessOpen}
        onClose={() => setIsSuccessOpen(false)}
        onDone={() => setIsSuccessOpen(false)}
        doneLabel="Done"
        modalClassName="h-auto min-h-0 w-[460px] max-w-[92vw]"
      >
        <div className="space-y-3 text-center">
          <h2 className="text-2xl font-semibold text-[#071C50]">Success</h2>
          <p className="text-sm text-slate-600">{successMessage}</p>
        </div>
      </SuccessModal>
    </div>
  );
};

export default Tools;
